import { Center, HeaderBox, HorizBox, Pad, PadBox, WindowTitle } from 'component/basics';
import { CTAButton, TextButton } from 'component/button';
import { colorEntryPointColorGreen, colorTextOnAccentColorBg, colorTextSecondary } from 'component/color';
import type { CommentConfig } from 'component/comment';
import { CommentBoosterArgs, commentTable } from 'component/comment';
import { spacings } from 'component/constants';
import { FormField } from 'component/form';
import { Byline } from 'component/people';
import { ConversationScreen } from 'component/screen';
import { Heading, TextField, UtilityText } from 'component/text';
import { CommunityGuidelines } from 'feature/question/community-guidelines';
import React, { useState } from 'react';
import { View } from 'react-native';
import { useIsAdmin } from 'util/admin';
import { Datastore, useCollection, useDatastore, useGlobalProperty, useLogEvent, useModuleMap, useModuleObject } from 'util/datastore';
import { useConfig } from 'util/features';
import type { ModuleMap } from 'util/moduledata';
import { defineModuleMap } from 'util/moduledata';
import type { StandardConfig } from 'util/stdconfig';
import type { Structure } from 'util/stdtypes';
import { declareGlobalProperty, fromProperty, nameProperty, previewProperty, t } from 'util/stdtypes';

// HACK: Topic should not be directly using CommentConfig since topics do not
// always contain comments.
// CommentConfig should be exposed by the feature that adds comments.
// This should be removed from the TopicConfig once feature dependency is set correctly

export type TopicConfig = StandardConfig &
    CommentConfig & {
        headerBackgroundColor: string;
        headerFontColor: string;
        headerTopLeftWidgets: React.ComponentType[];
        commentsTopLeftWidgets: React.ComponentType[];
        commentsTopRightWidgets: React.ComponentType[];
        commentsBottomWidgets: React.ComponentType[];
        topicContentWidgets: React.ComponentType[];
        topicTopWidgets: React.ComponentType[];
        topicBottomWidgets: React.ComponentType[];
        topicHeaderLeftWidgets: React.ComponentType[];
        topicHeaderRightWidgets: React.ComponentType[];
        topicHeaderBottomWidgets: React.ComponentType[];
        pageTopLeftWidgets: React.ComponentType[];
        pageTopRightWidgets: React.ComponentType[];
        pageBottomWidgets: React.ComponentType[];
        pageHiddenWidgets: React.ComponentType[];
        teaser: React.ComponentType | null;
        introHeaderTopRightWidgets: React.ComponentType[];
    };

export const TopicStructure: Structure<TopicConfig> = {
    key: 'topic',
    name: 'Topic',
    screen: TopicScreen,
    teaser: TopicTeaser,
    hasFocusTrap: true,
    subscreens: {
        topicNameAdmin: () => <TopicNameAdminScreen />,
    },
    receivesConfigDefaults: {
        topicContentWidgets: [],
        topicTopWidgets: [],
        topicBottomWidgets: [],
        topicHeaderLeftWidgets: [],
        topicHeaderRightWidgets: [],
        topicHeaderBottomWidgets: [],
        pageTopLeftWidgets: [],
        pageTopRightWidgets: [],
        pageBottomWidgets: [],
        pageHiddenWidgets: [],
        headerTopLeftWidgets: [],
        teaser: null,
        topBarHelpBubbles: [],
        bylineTags: [],
        profilePhotoLayers: [],
        composerTopWidgets: [],
        commentMiddleWidgets: [],
        commentAboveWidgets: [],
        commentBodyStylers: [],
        commentPostTriggers: [],
        commentPostActionLabel: null,
        commentPostBlockers: [],
        commentPostCheckers: [],
        replyAboveWidgets: [],
        replyBooster: undefined,
        commentBooster: undefined,
        headerBackgroundColor: colorEntryPointColorGreen,
        headerFontColor: colorTextOnAccentColorBg,
        commentsTopLeftWidgets: [],
        commentsTopRightWidgets: [],
        commentsBottomWidgets: [],
        composerPreview: null,
        readOnly: false,
        commentDeleteTriggers: [],
        sidebarCloseModal: null,
        sidebarShouldShowCloseModal: null,
        commentFilters: [],
        commentRankers: [],
        commentLimit: null,
        commentActions: [],
        commentRightActions: [],
        commentTopWidgets: [],
        commentBelowWidgets: [],
        commentBodyRenderer: null,
        commentStylers: [],
        commentAllowEmpty: false,
        commentAllowMultiple: false,
        commentEditBottomWidgets: [],
        commentEditTopWidgets: [],
        commentReplyPlaceholder: t('Reply to {authorName}...'),
        commentInputPlaceholder: t('Share your thoughts...'),
        commentInputLoginAction: '',
        composerSubtitle: () => t('Public Comment'),
        noMoreCommentsMessage: t('No more responses'),
        noCommentsTitle: t('Start the conversation'),
        noCommentsMessage: t('Be the first to share your thoughts'),
        composerTopBanners: [],
        replyFilters: [],
        pageTopWidgets: [],
        pageShowEmptyHelp: false,
        topBanners: [],
        introHeaderTopRightWidgets: [CommunityGuidelines],
    },
};

export const startAtProperty = declareGlobalProperty<string>('startAt');
export const endAtProperty = declareGlobalProperty<string>('endAt');

export function TopicTeaser() {
    const { teaser } = useConfig<TopicConfig>();
    if (teaser) {
        return React.createElement(teaser);
    } else {
        return <UtilityText label={t('No teaser defined')} />;
    }
}

function TopicHeader() {
    const datastore = useDatastore();
    const name = useGlobalProperty(nameProperty);
    const from = useGlobalProperty(fromProperty);
    const isAdmin = useIsAdmin();
    const { topicHeaderLeftWidgets, topicHeaderRightWidgets, topicHeaderBottomWidgets, headerBackgroundColor, headerFontColor } =
        useConfig<TopicConfig>();
    return (
        <View>
            <HeaderBox backgroundColor={headerBackgroundColor} bottom={32} top={16}>
                <WindowTitle text={name ?? ''} />
                {topicHeaderLeftWidgets?.length || topicHeaderRightWidgets?.length ? <Pad size={12} /> : null}
                <HorizBox center spread>
                    <HorizBox center>
                        {topicHeaderLeftWidgets.map((Widget, index) => (
                            <Widget key={index} />
                        ))}
                    </HorizBox>
                    <HorizBox center>
                        {topicHeaderRightWidgets.map((Widget, index) => (
                            <Widget key={index} />
                        ))}
                    </HorizBox>
                </HorizBox>
                {topicHeaderLeftWidgets?.length || topicHeaderRightWidgets?.length ? <Pad size={spacings.xl} /> : <Pad size={12} />}
                <Pad size={12} />
                <Heading color={headerFontColor} text={name ?? ''} type='large' weight='medium' />
                {isAdmin && (
                    <TextButton
                        color={colorTextSecondary}
                        label={t('Edit topic name (admin)')}
                        onPress={() => datastore.pushSubscreen('topicNameAdmin')}
                        type={'tiny'}
                    />
                )}
                <Pad size={20} />
                {topicHeaderBottomWidgets.map((Widget, index) => (
                    <Widget key={index} />
                ))}
            </HeaderBox>
            <Pad size={20} />
        </View>
    );
}

export function TopicScreen() {
    const { topicTopWidgets, topicContentWidgets, topicBottomWidgets, topBanners } = useConfig<TopicConfig>();
    const topicBottomWidgetsLastIndex = topicBottomWidgets.length - 1;
    const isLastEditorialReadingListWidget = topicBottomWidgets[topicBottomWidgetsLastIndex]?.name === 'EditorialReadingListWidget';
    useLogEvent('topic-view');
    return (
        <ConversationScreen>
            {topBanners.map((Widget, index) => (
                <Widget key={index} />
            ))}
            <TopicHeader />
            {topicTopWidgets.map((Widget, index) => (
                <Widget key={index} />
            ))}
            {topicContentWidgets.map((Widget, index) => (
                <Widget key={index} />
            ))}
            {topicBottomWidgets.map((Widget, index) => (
                <Widget key={index} />
            ))}
            {!isLastEditorialReadingListWidget && <Pad size={80} />}
        </ConversationScreen>
    );
}

function TopicNameAdminScreen() {
    const datastore = useDatastore();
    const initialName = useGlobalProperty(nameProperty);
    const initialPreview = useGlobalProperty(previewProperty);
    const [name, setName] = useState(initialName);
    const isAdmin = useIsAdmin();

    const updateTopicName = () => {
        datastore.setGlobalProperty(nameProperty, name);
        datastore.setGlobalProperty(previewProperty, { ...initialPreview, name });
        datastore.goBack();
    };

    if (!isAdmin) {
        return <UtilityText text={'Access denied'} />;
    }

    return (
        <ConversationScreen>
            <PadBox horiz={spacings.lg} vert={spacings.xl2}>
                <Heading label={t('Edit topic name (admin)')} type='large' weight='medium' />
                <PadBox vert={spacings.lg}>
                    <FormField label={t('Topic name')}>
                        <TextField multiline={false} onChange={setName} placeholderLabel={t('Topic name')} value={name} />
                    </FormField>
                </PadBox>
                <CTAButton label={t('Update')} onPress={updateTopicName} />
            </PadBox>
        </ConversationScreen>
    );
}

type Topic = {
    name: string;
    from: string;
    time: number;
    photoUrl?: string;
};
export const moduleMapTopic: ModuleMap<Topic> = defineModuleMap('PUBLIC', 'topic', null);

export function useGlobalTopics() {
    const topicObjs = useModuleMap(moduleMapTopic);
    if (!topicObjs) {
        return [];
    }
    const keys = Object.keys(topicObjs);
    const topics = keys.map((key) => ({ key, ...topicObjs[key] }));
    return topics;
}

export function useTopic(key: string) {
    const topic = useModuleObject(moduleMapTopic, key);
    return topic;
}
